import { useState, type MouseEvent } from 'react';
import { MessageSquarePlus, Send, Star, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useCreateCourseFeedbackMutation } from '@/state/course';
import { cn } from '@/lib/utils';

interface CourseFeedbackFloatingProps {
  tenant: string;
  courseId: number;
}

const MAX_RATING = 5;

const CourseFeedbackFloating = ({ tenant, courseId }: CourseFeedbackFloatingProps) => {
  const [open, setOpen] = useState(false);
  const [rating, setRating] = useState(0);
  const [hoverRating, setHoverRating] = useState<number | null>(null);
  const [feedback, setFeedback] = useState('');
  const [anonymous, setAnonymous] = useState(true);
  const [createCourseFeedback, { isLoading }] = useCreateCourseFeedbackMutation();

  const resetForm = () => {
    setRating(0);
    setHoverRating(null);
    setFeedback('');
    setAnonymous(true);
  };

  const resolveRatingFromPointer = (index: number, event: MouseEvent<HTMLButtonElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const clickX = event.clientX - rect.left;
    return clickX <= rect.width / 2 ? index - 0.5 : index;
  };

  const handleStarClick = (index: number, event: MouseEvent<HTMLButtonElement>) => {
    const nextRating = resolveRatingFromPointer(index, event);
    setRating(nextRating);
  };

  const handleStarHover = (index: number, event: MouseEvent<HTMLButtonElement>) => {
    const nextRating = resolveRatingFromPointer(index, event);
    setHoverRating(nextRating);
  };

  const handleSubmit = async () => {
    if (!tenant || !courseId) return;
    const cleanFeedback = feedback.trim();
    if (!cleanFeedback) {
      toast.error('Feedback is required.');
      return;
    }
    if (rating <= 0) {
      toast.error('Please provide a rating.');
      return;
    }
    try {
      await createCourseFeedback({
        tenant,
        data: {
          feedback: cleanFeedback,
          rating,
          anonymous,
          course: courseId,
        },
      }).unwrap();
      toast.success('Feedback submitted successfully.');
      setOpen(false);
      resetForm();
    } catch {
      toast.error('Failed to submit feedback.');
    }
  };

  return (
    <>
      <button
        type="button"
        className="fixed bottom-4 right-4 z-50 inline-flex items-center gap-2 rounded-[6px] border border-primary/40 bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-lg transition-colors hover:bg-primary/90"
        onClick={() => setOpen(true)}
      >
        <MessageSquarePlus className="h-4 w-4" />
        <p className='hidden md:block'>Give feedback</p>
      </button>

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen);
          if (!nextOpen) resetForm();
        }}
      >
        <DialogContent className="sm:max-w-[640px]" showCloseButton={false}>
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>Add feedback</DialogTitle>
              <button
                type="button"
                className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-muted/70 hover:bg-muted"
                onClick={() => setOpen(false)}
              >
                <X className="h-4 w-4" />
                <span className="sr-only">Close</span>
              </button>
            </div>
            <DialogDescription className="sr-only">Submit course feedback.</DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <p className="text-2xl font-medium">How was your experience?</p>
              <div className="mt-3 flex items-center gap-1">
                {Array.from({ length: MAX_RATING }, (_, index) => {
                  const position = index + 1;
                  const displayRating = hoverRating ?? rating;
                  const fill = Math.max(0, Math.min(1, displayRating - (position - 1)));
                  return (
                    <button
                      key={position}
                      type="button"
                      className="relative h-7 w-7"
                      onClick={(event) => handleStarClick(position, event)}
                      onMouseMove={(event) => handleStarHover(position, event)}
                      onMouseLeave={() => setHoverRating(null)}
                      aria-label={`Rate ${position} stars`}
                    >
                      <Star className="h-7 w-7 text-muted-foreground/40" />
                      <span
                        className="absolute inset-y-0 left-0 overflow-hidden"
                        style={{ width: `${fill * 100}%` }}
                      >
                        <Star className="h-7 w-7 fill-amber-400 text-amber-400" />
                      </span>
                    </button>
                  );
                })}
                <span className="ml-2 text-sm text-muted-foreground">
                  {(hoverRating ?? rating).toFixed(1)}
                </span>
              </div>
            </div>

            <div className="space-y-2">
              <label className="text-sm font-medium">Add feedback</label>
              <Textarea
                value={feedback}
                onChange={(event) => setFeedback(event.target.value)}
                placeholder="Add feedback"
                className="min-h-[120px] resize-none"
              />
            </div>

            <div className="flex items-center justify-between px-1 py-1">
              <p className="text-sm font-medium">Send anonymously</p>
              <button
                type="button"
                role="switch"
                aria-checked={anonymous}
                onClick={() => setAnonymous((prev) => !prev)}
                className={cn(
                  'relative inline-flex h-6 w-11 shrink-0 cursor-pointer items-center rounded-full transition-colors',
                  anonymous ? 'bg-primary' : 'bg-muted-foreground/30'
                )}
              >
                <span
                  className={cn(
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-background shadow-sm ring-0 transition-transform',
                    anonymous ? 'translate-x-5' : 'translate-x-0.5'
                  )}
                />
              </button>
            </div>
          </div>

          <DialogFooter className="flex items-center justify-end gap-2">
            <Button type="button" onClick={() => void handleSubmit()} disabled={isLoading}>
              <Send className="h-4 w-4" />
              Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CourseFeedbackFloating;
